import React, { Fragment, useCallback, useState, useMemo } from 'react';
import { Box, CircularProgress, Container, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
//
import FilterPanel from 'components/reports/balances/FilterPanel';
import ReportTable from 'components/reports/balances/ReportTable';
import Title from 'components/common/Title';
import { showErrorNotification } from 'utils/common';
import {
  useDownloadBalancesReportMutation,
  useGetBalancesReportQuery,
  useGetBalancesReportTotalsQuery,
} from '../../../store/api/report.api';
import { useBlobDownload } from 'hooks/utils/useBlobDownload';
import { useApiErrorSnackbar } from 'hooks/utils/useApiErrorSnackbar';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  overflow: 'auto',
  flexDirection: 'column',
}));
const REPORT_PLACEHOLDER = {
  rows: [],
  count: 0,
  totals: null,
};

const BalancesReportPage = () => {
  const [paginationModel, setPaginationModel] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const [currentFilters, setCurrentFilters] = useState({});

  const [sorting, setSorting] = useState([{ id: 'client', desc: true }]);

  // params,shared across all requests
  const coreFilterParams = useMemo(() => {
    const { ts, ...filters } = currentFilters || {};
    return {
      ts, // exposing timestamp prop to upper level, so it could be easily destructured and omitted later
      filters,
    };
  }, [currentFilters]);

  // params for report downloading and displaying
  const downloadReportParams = useMemo(
    () => ({
      ...coreFilterParams,
      sorting,
    }),
    [coreFilterParams, sorting],
  );

  // params for displaying reports - same as download + pagination
  const showReportParams = useMemo(
    () => ({
      ...downloadReportParams,
      page: paginationModel.pageIndex,
      limit: paginationModel.pageSize,
    }),
    [downloadReportParams, paginationModel.pageIndex, paginationModel.pageSize],
  );

  const {
    data: { rows, count } = REPORT_PLACEHOLDER,
    error,
    isFetching: isLoadingReport,
    isLoading: isReportsNotLoaded,
  } = useGetBalancesReportQuery(showReportParams);

  const {
    data: totals = null,
    error: totalsError,
    isFetching: isLoadingTotals,
    isLoading: isTotalsNotLoaded,
  } = useGetBalancesReportTotalsQuery(coreFilterParams);

  useApiErrorSnackbar(error);
  useApiErrorSnackbar(totalsError);
  const [downloadReport, { isLoading: isDownloadingReport }] =
    useDownloadBalancesReportMutation();
  const downloadBlob = useBlobDownload();

  const isRequestPending = isLoadingReport || isDownloadingReport;

  const applyFilters = useCallback(
    (filtersProps) => {
      setCurrentFilters({ ...filtersProps, ts: Date.now() });
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [setCurrentFilters, setPaginationModel],
  );

  const handleDownloadReport = useCallback(async () => {
    try {
      const data = await downloadReport(downloadReportParams).unwrap();
      downloadBlob(data, 'balances.csv');
    } catch (err) {
      if (err.response) {
        const blob = err.response.data;
        if (blob instanceof Blob) {
          const errorText = await blob.text();

          const parsedError = JSON.parse(errorText);

          showErrorNotification(parsedError.error);
        }
      } else {
        console.error(`Error download report: ${err.message}`);
      }
    }
  }, [downloadReport, downloadReportParams, downloadBlob]);

  return (
    <Fragment>
      <Container
        maxWidth='100%'
        sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        <StyledPaper sx={{ overflow: 'hidden' }}>
          <Box>
            <Title>Balances Report</Title>
          </Box>
          <FilterPanel
            applyFilters={applyFilters}
            isRequestPending={isRequestPending}
          />
        </StyledPaper>

        {isReportsNotLoaded || isTotalsNotLoaded ? (
          <CircularProgress
            color='primary'
            size='4rem'
            sx={{ mx: 'auto', my: '2rem' }}
          />
        ) : (
          <Paper>
            <ReportTable
              rowData={rows}
              count={count}
              totals={totals}
              paginationModel={paginationModel}
              sorting={sorting}
              onSortingChange={setSorting}
              onPaginationChange={setPaginationModel}
              onDownload={handleDownloadReport}
              isRequestPending={isLoadingReport || isLoadingTotals}
            />
          </Paper>
        )}
      </Container>
    </Fragment>
  );
};

export default BalancesReportPage;
